import React, { useState, useEffect } from 'react';
import { useUserContext } from '../contexts/UserContext';
import { useUserDBContext } from '../contexts/UserDBContext';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import './style/CreateEvent.css';
import { useEventContext } from '../contexts/EventContext';
import { SUBSCRIPTION_LIMITS } from '../constants/subscriptionRules';
import { Breadcrumbs } from '@aws-amplify/ui-react';
import { MdChevronRight } from 'react-icons/md';
import LoadingLogo from '../components/LoadingLogo';
import { useTranslation } from 'react-i18next';

function InfoBulle({ text }) {
  return (
    <span className="info-bulle-wrapper">
      <span
        className="info-bulle-icon"
        tabIndex={0} // pour focus clavier/accessibilité
        aria-label="Informations"
      >
        i
      </span>
      <span className="info-bulle-tooltip">{text}</span>
    </span>
  );
}


function CreateEvent() {
  const [eventName, setEventName] = useState('');
  const [eventType, setEventType] = useState('livephoto');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [eventIdGenerated, setEventIdGenerated] = useState('');

  const { tokens } = useUserContext();
  const { user } = useAuthenticator((context) => [context.user]);
  const { userDBData, refetchUserDB, loadingUserDB } = useUserDBContext();
  const navigate = useNavigate();
  const { events, refetchEvents } = useEventContext();
  const { t } = useTranslation();

  const isLoading = !userDBData || !events;

  // 🔄 Refresh des events si on revient sur l'onglet
  useEffect(() => {
    const handleFocus = () => {
      refetchEvents();
      refetchUserDB(); // 🔁 force la mise à jour du user
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetchEvents, refetchUserDB]);

  if (isLoading) {
    return (
      <div className="create-event-page">
        
          <LoadingLogo />
        
      </div>
    );
  }

  const subscription = userDBData?.subscriptions;
  const subscriptionOneTime = String(userDBData?.subscriptions_onetime).toLowerCase() === 'true';




  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  const eventsThisMonth = events?.filter((event) => {
    const dateRaw = event.createdAt || '';
    const datePart = dateRaw.split(' ')[0];
    if (!datePart || !datePart.includes('/')) {
      console.warn("❌ Format invalide pour createdAt :", dateRaw);
      return false;
    }

    const [day, month, year] = datePart.split('/');
    const eventDate = new Date(`${year}-${month}-${day}`);

    if (isNaN(eventDate.getTime())) {
      console.warn("❌ Date incorrecte :", eventDate, "→ source :", dateRaw);
      return false;
    }

    // Exclure les événements avec souscription P0 ou P2
    const sub = event.Sub || '';
    if (sub === 'P0' || sub === 'P2') {
      return false;
    }

    return eventDate.getMonth() === currentMonth && eventDate.getFullYear() === currentYear;
  }) || [];





  const maxEventsAllowed = SUBSCRIPTION_LIMITS[subscription]?.maxEventsPerMonth ?? 0;
  const hasReachedQuota = eventsThisMonth.length >= maxEventsAllowed;


  const finalHasReachedQuota = (
    (subscription === 'P2' && !subscriptionOneTime) ||
    (subscription !== 'P2' && hasReachedQuota)
  );


  const generateToday = () => {
    const now = new Date();
    const date = now.toLocaleDateString('fr-FR');
    const time = now.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${date} ${time}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    const newEventId = uuidv4();

    const payload = {
      eventId: newEventId,
      eventName,
      eventType,
      subscription,
      createdAt: generateToday(),
      createdBy: user?.username || 'unknown',
    };

    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const endpointevents = `${baseUrl}/create-events`; // Endpoint pour créer un événement
      const response = await axios.post(
        endpointevents,
        JSON.stringify(payload),
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokens.accessToken}`,
          },
        }
      );

      let data = {};
      const body = response?.data?.body;
      if (body) {
        try {
          data = JSON.parse(body);
        } catch (e) {
          console.error("[CreateEvent] Erreur de parsing JSON : ", e, "body reçu : ", body);
          throw new Error("Erreur lors du parsing JSON du retour création événement.");
        }
      } else if (typeof response.data === "object" && response.data !== null) {
        data = response.data;
      } else {
        console.warn("[CreateEvent] Réponse backend inattendue :", response.data);
        throw new Error("Donnée de réponse création événement vide ou mal formatée.");
      }

      console.log("[CreateEvent] Réponse backend :", data);

      setSuccessMessage(data.message || 'Événement créé avec succès.');
      setEventIdGenerated(data.item?.eventId || newEventId);
      setEventName('');
      setEventType('livephoto');
      refetchEvents();
      refetchUserDB();

    } catch (error) {
      setErrorMessage("Erreur lors de la création de l’événement.");
    }
  };

  const isBlocked = subscription === 'P1';


  return (
    <>
    <div style={{ marginTop: '10px', marginLeft: '10%' }}>
      <Breadcrumbs
        items={[
          { label: '' }, // non cliquable
          { href: '/events', label: t("Event") },
          { label: t("CreateEvent") }
        ]}
        separator={<MdChevronRight style={{ color: '#facc15' }} />}
      />
    </div>

    <div className="create-event-page">
      <div className="create-event-container">
        

        <h2>{t("createevent.title")}</h2>

        {!eventIdGenerated && (
          <>
            {isBlocked || finalHasReachedQuota ? (
              <div className="blocked-access">
                <p className="error-message">
                  {isBlocked
                    ? t("createevent.blocked.license")
                    : t("createevent.blocked.quota", { max: maxEventsAllowed })}
                </p>
                <p>{t("createevent.blocked.upgrade")}</p>
                <button onClick={() => navigate('/pricing')}>
                  {t("createevent.blocked.button")}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <input
                  type="text"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  placeholder={t("createevent.form.namePlaceholder")}
                  required
                  maxLength={40}
                />
                <div className="radio-group">
                  {[
                    {
                      label: t("createevent.form.types.photo.label"),
                      value: 'photopredefini',
                      info: t("createevent.form.types.photo.info")
                    },
                    {
                      label: t("createevent.form.types.livephoto.label"),
                      value: 'livephoto',
                      info: t("createevent.form.types.livephoto.info")
                    },
                    {
                      label: t("createevent.form.types.livetext.label"),
                      value: 'livetext',
                      info: t("createevent.form.types.livetext.info")
                    },
                    {
                      label: t("createevent.form.types.livephototext.label"),
                      value: 'livephototext',
                      info: t("createevent.form.types.livephototext.info")
                    }
                  ].map(({ label, value, info }) => (
                    <label
                      key={value}
                      className={`radio-option ${eventType === value ? 'active' : ''}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <input
                        type="radio"
                        value={value}
                        checked={eventType === value}
                        onChange={(e) => setEventType(e.target.value)}
                      />
                      <span>{label}</span>
                      <InfoBulle text={info} />
                    </label>
                  ))}
                </div>
                <button type="submit">{t("createevent.form.submit")}</button>
              </form>
            )}

            {errorMessage && <p className="error-message">{t("createevent.form.error")}</p>}
          </>
        )}

        {eventIdGenerated && (
          <div className="qr-container">
            <p className="success-message">{t("createevent.success.message")}</p>
            <QRCodeSVG value={`${window.location.origin}/${eventIdGenerated}/upload`} size={180} />
            <p className="qr-link">
              URL :
              <a href={`${window.location.origin}/${eventIdGenerated}/upload`} target="_blank" rel="noreferrer">
                {`${window.location.origin}/${eventIdGenerated}/upload`}
              </a>
            </p>
            <button className="navigate-button" onClick={() => navigate('/events')}>
              {t("createevent.success.button")}
            </button>
          </div>
        )}


      </div>
    </div>
    </>
  );
}

export default CreateEvent;
